import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPresignedUrlAPI,
  updateInpectionPointApi,
  updateInspectionToProgressApi,
  updatePhotoUrlAPI,
} from "../services/inspectionAPI";
import { AppDispatch, RootState } from "../store";
import {
  updateAssetStatus,
  updateWorkOrderDetail,
  updateWorkOrderDetailWithModule,
} from "./workOrderDetailSlice";
import { WorkOrderStatus } from "../../types/workOrder";
import { isReallyOnline } from "../../lib/isOnline";
import { showToaster } from "./toasterSlice";
import { Detail, Section } from "../../types/inspection";
import { isPointCompleted } from "../../lib/inspectionItems";

export interface UpdateInspectionDetailState {
  loading: boolean;
  error: string | null;
}

export interface UpdateWorkOrderAndInspectionProps {
  item_ID: number;
  updatePoint: Detail;
  sectionIndex: number;
  token: string | null;
  workorder_id: number;
  shouldWorkOrderMoveToProgress?: boolean;
}

export interface UpdateWorkOrderAndModuleProps {
  item_ID: number;
  updatedModule: Section;
  token: string | null;
  sectionIndex: number;
  workorder_id: number;
  shouldWorkOrderMoveToProgress?: boolean;
}

const initialState: UpdateInspectionDetailState = {
  loading: false,
  error: null,
};

export const updateInspectionPoint = createAsyncThunk<
  string,
  {
    token: string | null;
    inspection_pont_id: number;
    data: any;
    workorder_id: number;
    shouldNotify?: boolean;
  },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "inspectionPoint/updateInspectionPoint",
  async (
    { token, inspection_pont_id, data, workorder_id, shouldNotify = true },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const { workOrders } = getState().workOrders;
      const matchingOrder = workOrders.find(
        (order) => order.workOrder_ID === workorder_id
      );
      if (
        matchingOrder &&
        matchingOrder.workOrderStatus == WorkOrderStatus.Completed
      )
        return;
      const updatedInpectionPointResult = await updateInpectionPointApi(
        token,
        inspection_pont_id,
        data
      );
      if (shouldNotify) {
        const isOnline = await isReallyOnline();
        dispatch(
          showToaster({
            message: isOnline
              ? "Inspection point updated"
              : "Inspection point updated offline",
            type: "success",
          })
        );
      }

      return updatedInpectionPointResult;
    } catch (error: any) {
      dispatch(
        showToaster({ message: "Error while updating point", type: "error" })
      );

      console.log("[Error in updating inspection point]:: ", error?.message);
      return rejectWithValue("Failed to update inspection point");
    }
  }
);

export const updateInpectionPointAndStartWorkOrder =
  (props: UpdateWorkOrderAndInspectionProps) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      item_ID,
      updatePoint,
      token,
      workorder_id,
      sectionIndex,
      shouldWorkOrderMoveToProgress = true,
    } = props;

    try {
      const { workOrders } = getState().workOrders;
      const matchingOrder = workOrders.find(
        (order) => order.workOrder_ID === workorder_id
      );
      if (
        matchingOrder &&
        matchingOrder.workOrderStatus == WorkOrderStatus.Completed &&
        shouldWorkOrderMoveToProgress
      )
        return;
      dispatch(
        updateWorkOrderDetail({
          item_ID,
          sectionIndex,
          updatePoint,
          workorder_id,
        })
      );
      const item = getState().workOrderDetail.workOrderDetail[
        workorder_id
      ].items?.find((order) => order.item_ID === item_ID);
      const points = item?.modules[sectionIndex].points;
      const matchingPoint = points.find(
        (point: any) => point.id === updatePoint.id
      );
      if (item) {
        if (
          item.totalVerifiedModules === item?.modules?.length &&
          item.status_ID != 3
        ) {
          await dispatch(
            updateAssetStatus({
              token,
              asset_id: item?.id,
              status: 3,
              workOrder_ID: workorder_id,
              data: {
                metaData: {
                  workOrderId: workorder_id,
                },
              },
              shouldUpdateCompleteAssetCount: !(
                matchingPoint.isCompleted &&
                updatePoint.notApplicable &&
                !matchingPoint.notApplicable
              ),
            })
          );
        }
        if (
          item.totalVerifiedModules !== item?.modules?.length &&
          item.status_ID == 3
        ) {
          const isCompleted = isPointCompleted(updatePoint, item.type);
          await dispatch(
            updateAssetStatus({
              token,
              asset_id: item?.id,
              status: isCompleted ? 3 : 1,
              workOrder_ID: workorder_id,
              data: {
                metaData: {
                  workOrderId: workorder_id,
                },
              },
              shouldUpdateCompleteAssetCount: !(
                matchingPoint.isCompleted &&
                !updatePoint.notApplicable &&
                matchingPoint.notApplicable
              ),
            })
          );
        }
      }
      if (
        matchingOrder &&
        matchingOrder.workOrderStatus == WorkOrderStatus.Scheduled &&
        shouldWorkOrderMoveToProgress
      ) {
        await dispatch(updateInspectionToProgress({ token, workorder_id }));
      }
    } catch (error) {
      console.error(
        "Error updating work order and inspection progress:",
        error
      );
    }
  };

export const updateModuleAndStartWorkOrder =
  (props: UpdateWorkOrderAndModuleProps) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      item_ID,
      updatedModule,
      token,
      workorder_id,
      sectionIndex,
      shouldWorkOrderMoveToProgress = true,
    } = props;

    try {
      const { workOrders } = getState().workOrders;
      const matchingOrder = workOrders.find(
        (order) => order.workOrder_ID === workorder_id
      );
      // if (matchingOrder && matchingOrder.workOrderStatus == WorkOrderStatus.Completed && shouldWorkOrderMoveToProgress)
      //     return
      dispatch(
        updateWorkOrderDetailWithModule({
          item_ID,
          updatedModule,
          sectionIndex,
          workorder_id,
        })
      );
      const item = getState().workOrderDetail.workOrderDetail[
        workorder_id
      ].items?.find((order) => order.item_ID === item_ID);

      if (item) {
        if (
          item.totalVerifiedModules === item?.modules?.length &&
          item.status_ID != 3
        ) {
          await dispatch(
            updateAssetStatus({
              token,
              asset_id: item?.id,
              status: 3,
              workOrder_ID: workorder_id,
              data: {
                metaData: {
                  workOrderId: workorder_id,
                  description: `failed to mark asset completed.`,
                },
              },
              shouldUpdateCompleteAssetCount: true,
            })
          );
        }
        if (
          item.totalVerifiedModules !== item?.modules?.length &&
          item.status_ID == 3
        ) {
          const isCompletedAllPoints = updatedModule.points.every(
            (point: any) => point.isCompleted === true
          );

          await dispatch(
            updateAssetStatus({
              token,
              asset_id: item?.id,
              status: isCompletedAllPoints ? 3 : 1,
              workOrder_ID: workorder_id,
              data: {
                metaData: {
                  workOrderId: workorder_id,
                  description: `failed to mark asset ${
                    isCompletedAllPoints ? "completed" : "not completed"
                  }.`,
                },
              },
              shouldUpdateCompleteAssetCount: true,
            })
          );
        }
      }
      if (
        matchingOrder &&
        matchingOrder.workOrderStatus == WorkOrderStatus.Scheduled &&
        shouldWorkOrderMoveToProgress
      ) {
        await dispatch(updateInspectionToProgress({ token, workorder_id }));
      }
    } catch (error) {
      console.error(
        "Error updating work order and inspection progress:",
        error
      );
    }
  };

export const updateInspectionToProgress = createAsyncThunk<
  string,
  { token: string | null; workorder_id: number },
  { rejectValue: string }
>(
  "inspectionPoint/updateInspectionToProgress",
  async ({ token, workorder_id }, { rejectWithValue }) => {
    try {
      const updatedWordOrderResult = await updateInspectionToProgressApi(
        token,
        workorder_id
      );
      return updatedWordOrderResult;
    } catch (error) {
      console.log("[Error in updating work order to progress]:: ", error);
      return rejectWithValue("Failed to update updateInspectionToProgressApi");
    }
  }
);

export const fetchPresignedUrl = createAsyncThunk<
  { url: string },
  { token: string | null; fileName: string },
  { rejectValue: string }
>(
  "assets/fetchPresignedUrl",
  async ({ token, fileName }, { rejectWithValue }) => {
    try {
      if (token) {
        const presignedUrl = await getPresignedUrlAPI(token, fileName);
        return presignedUrl;
      } else {
        return rejectWithValue("Invalid token");
      }
    } catch (error: any) {
      console.log("[Error in Fetching presigned url]:: ", error);
      if (error?.code === "ERR_NETWORK") {
        return rejectWithValue("Network error");
      }
      return rejectWithValue("Failed to fetch presigned url");
    }
  }
);

export const updatePhotoUrl = createAsyncThunk<
  string,
  { token: string | null; inspectionPointId: number; url: string; displayOrder: number },
  { rejectValue: string; dispatch: AppDispatch }
>(
  "inspectionPoint/updatePhotoUrl",
  async ({ token, inspectionPointId, url, displayOrder }, { rejectWithValue }) => {
    try {
      const updatedPhotoUrlResult = await updatePhotoUrlAPI(
        token,
        inspectionPointId,
        url,
        displayOrder
      );
      // dispatch(showToaster({ message: "Photo URL updated successfully", type: "success" }));
      return updatedPhotoUrlResult;
    } catch (error: any) {
    //   dispatch(
    //     showToaster({ message: "Error updating photo URL", type: "error" })
    //   );
      return rejectWithValue("Failed to update photo URL");
    }
  }
);

const inspectionPointSlice = createSlice({
  name: "inspectionPoint",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateInspectionPoint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInspectionPoint.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateInspectionPoint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePhotoUrl.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePhotoUrl.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updatePhotoUrl.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default inspectionPointSlice.reducer;
